import { useState } from "react";

import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { PreflightDiagnosticControl } from "./preflight-diagnostic-control.js";
import { AiAnalysisSettingsPanel } from "./ai-analysis-settings-panel.js";
import { MarketDataSettingsPanel } from "./market-data-settings-panel.js";
import { useLiveScheduleStatus, useSetLiveScheduleEnabled, useSystemStatus } from "./queries.js";

const dependencyLabels = {
  backtests: "Backtest jobs",
  broker: "Tastytrade broker",
  database: "Trading database",
  "live-runtime": "Live runtime",
  "market-data": "Alpaca credentials and calendar",
  queue: "Backtest queue",
  storage: "Artifact storage",
} as const;

export function AdminPage() {
  const status = useSystemStatus();
  const liveSchedules = useLiveScheduleStatus();
  const setLiveScheduleEnabled = useSetLiveScheduleEnabled();
  const [confirmation, setConfirmation] = useState<boolean | null>(null);
  const blockers = liveSchedules.data?.blockers;
  const blockerCount =
    blockers === undefined
      ? 0
      : blockers.activeBots +
        blockers.activeRuns +
        blockers.openPositions +
        blockers.unresolvedOrders;
  const changeScheduleState = async () => {
    if (confirmation === null) return;
    try {
      await setLiveScheduleEnabled.mutateAsync(confirmation);
      setConfirmation(null);
    } catch {
      // The mutation renders the API problem while preserving the confirmation context.
    }
  };
  return (
    <section className="page-stack page-stack--compact" aria-label="Admin">
      <MarketDataSettingsPanel />
      <AiAnalysisSettingsPanel />
      {new URLSearchParams(window.location.search).get("diagnostic") === "preflight" ? (
        <PreflightDiagnosticControl />
      ) : null}
      <article className="content-card form-stack" aria-labelledby="live-schedule-title">
        <div className="connection-heading">
          <div>
            <p className="eyebrow">Global live automation</p>
            <h2 id="live-schedule-title">Market-hour Lambda schedules</h2>
            <p className="section-description">
              Controls all six AWS schedules for live lifecycle and market data. Enabled schedules
              only run from 9:30 AM through 4:00 PM Eastern, Monday through Friday.
            </p>
          </div>
          <Button disabled={status.isFetching} onClick={() => void status.refetch()} tone="quiet">
            {status.isFetching ? "Refreshing…" : "Refresh status"}
          </Button>
        </div>
        {liveSchedules.isLoading ? (
          <ResourceState
            kind="loading"
            title="Reading AWS schedule state"
            description="The displayed state will reflect EventBridge Scheduler, not a local preference."
          />
        ) : null}
        {liveSchedules.isError ? (
          <ResourceState
            action={<Button onClick={() => void liveSchedules.refetch()}>Try again</Button>}
            kind="error"
            title="Live schedule state unavailable"
            description="No schedule change was made."
          />
        ) : null}
        {liveSchedules.data === undefined ? null : (
          <>
            <div className="status-overview">
              <div>
                <span
                  className={`status-pill status-pill--${
                    liveSchedules.data.state === "enabled"
                      ? "operational"
                      : liveSchedules.data.state === "disabled"
                        ? "unavailable"
                        : "degraded"
                  }`}
                >
                  {liveSchedules.data.state}
                </span>
                <h2>{liveSchedules.data.scheduleCount} AWS schedules</h2>
              </div>
              <p>Checked {new Date(liveSchedules.data.checkedAt).toLocaleString()}</p>
            </div>
            <dl className="detail-pairs">
              <div>
                <dt>Active Bots</dt>
                <dd>{liveSchedules.data.blockers.activeBots}</dd>
              </div>
              <div>
                <dt>Active live runs</dt>
                <dd>{liveSchedules.data.blockers.activeRuns}</dd>
              </div>
              <div>
                <dt>Open positions</dt>
                <dd>{liveSchedules.data.blockers.openPositions}</dd>
              </div>
              <div>
                <dt>Unresolved orders</dt>
                <dd>{liveSchedules.data.blockers.unresolvedOrders}</dd>
              </div>
            </dl>
            {confirmation === null ? (
              <div className="button-row">
                {liveSchedules.data.state !== "enabled" ? (
                  <Button onClick={() => setConfirmation(true)}>Turn on live schedules</Button>
                ) : (
                  <Button
                    disabled={blockerCount > 0}
                    onClick={() => setConfirmation(false)}
                    tone="danger"
                  >
                    Turn off live schedules
                  </Button>
                )}
                {blockerCount > 0 ? (
                  <p className="field-hint">
                    Stop all live Bots and resolve orders and positions before turning schedules
                    off.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="operation-status" role="alert">
                <strong>
                  {confirmation ? "Enable all live schedules?" : "Disable all live schedules?"}
                </strong>
                <span>
                  {confirmation
                    ? "Live Bots may resume at the next scheduled market-minute invocation."
                    : "No scheduled live-runtime or market-data Lambda will run while disabled."}
                </span>
                <div className="button-row">
                  <Button
                    disabled={setLiveScheduleEnabled.isPending}
                    onClick={() => void changeScheduleState()}
                    tone={confirmation ? "primary" : "danger"}
                  >
                    {setLiveScheduleEnabled.isPending ? "Applying…" : "Confirm"}
                  </Button>
                  <Button
                    disabled={setLiveScheduleEnabled.isPending}
                    onClick={() => setConfirmation(null)}
                    tone="quiet"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {setLiveScheduleEnabled.isError ? (
              <p className="form-message form-message--error">
                AWS schedule state could not be changed. Refresh to inspect the actual state.
              </p>
            ) : null}
          </>
        )}
      </article>
      {status.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading system status"
          description="Requesting the admin-authorized TradingEngine snapshot."
        />
      ) : null}
      {status.error !== null ? (
        <ResourceState
          kind="error"
          title="System status unavailable"
          description="The admin status endpoint could not be reached or did not authorize this request. No operational state is inferred."
          action={
            <Button onClick={() => void status.refetch()} tone="quiet">
              Retry
            </Button>
          }
        />
      ) : null}
      {status.data === undefined ? null : (
        <>
          <div className={`status-overview status-overview--${status.data.status}`}>
            <div>
              <span className={`status-pill status-pill--${status.data.status}`}>
                {status.data.status}
              </span>
              <h2>TradingEngine {status.data.status}</h2>
            </div>
            <p>Checked {new Date(status.data.checkedAt).toLocaleString()}</p>
          </div>
          {status.data.stale ? (
            <ResourceState
              kind="stale"
              title="Status snapshot is stale"
              description="The snapshot is more than one minute old. Refresh before using it for an operational decision."
            />
          ) : null}
          <div className="dependency-grid">
            {status.data.dependencies.map((dependency) => (
              <article
                className={`dependency-card dependency-card--${dependency.status}`}
                key={dependency.name}
              >
                <header>
                  <div>
                    <span className="dependency-icon" aria-hidden="true" />
                    <h2>{dependencyLabels[dependency.name]}</h2>
                  </div>
                  <span className={`status-pill status-pill--${dependency.status}`}>
                    {dependency.status}
                  </span>
                </header>
                <p>{dependency.message}</p>
                {Object.keys(dependency.details).length === 0 ? null : (
                  <dl className="detail-pairs">
                    {Object.entries(dependency.details).map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
